import { Component, OnInit } from '@angular/core';
import {
  AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {RegistrationService} from "../../../services/registration.service";
import { DataInput } from '@trivelope/triv-ui-tools-lib';


type PlanId = 'free' | 'basic' | 'comfort' | 'enterprise';

@Component({
  selector: 'app-authentication-register',
  templateUrl: './authentication-register.component.html',
  styleUrls: ['./authentication-register.component.css'],
})
export class AuthenticationRegisterComponent implements OnInit {
  // Formulare für Schritt 1 & 2
  startForm!: FormGroup;
  completeForm!: FormGroup;

  // UI-State
  bInProgress = false;
  bMailSent = false;       // nach Schritt 1 true
  errorMessage = '';

  // Token aus der E-Mail
  token: string | null = null;

  // Plan / CTA / Featureliste (wie zuvor)
  selectedPlan: PlanId = 'free';
  startCtaLabel = 'Kostenlos loslegen';
  selectedFeatures: string[] = [];

  private readonly PLAN_CONFIG: Record<PlanId, { cta: string; features: string[] }> = {
    free:    { cta: 'Kostenlos loslegen', features: [
        '5 Mitglieder','20 GB Speicherplatz','Vollständig eingerichtete Instanz in 24 Stunden',
        'Automatische Updates und Support','Wechselmöglichkeit zu kostenpflichtigem Abonnement bei größerem Bedarf',
        'SSL-Verschlüsselung','Kein benutzerdefiniertes Logo',
      ]},
    basic:   { cta: 'Mit Basic loslegen', features: [
        '100 Mitglieder','200 GB Speicherplatz','Vollständig eingerichtete Instanz in 24 Stunden',
        'Automatische Updates und Support','Tägliches Backup der Daten','Wechselmöglichkeit zu anderen Abonnements',
        'SSL-Verschlüsselung','Benutzerdefiniertes Logo','Benutzerdefinierte Farben',
      ]},
    comfort: { cta: 'Mit Comfort loslegen', features: [
        '500 Mitglieder','1000 GB Speicherplatz','Vollständig eingerichtete Instanz in 24 Stunden',
        'Automatische Updates und Support','Tägliches Backup der Daten','Wechselmöglichkeit zu anderen Abonnements',
        'SSL-Verschlüsselung','Benutzerdefiniertes Logo','Benutzerdefinierte Farben',
      ]},
    enterprise: { cta: 'Vertrieb kontaktieren', features: [
        'Unbegrenzte Anzahl Mitglieder','Unbegrenzter Speicherplatz','Vollständig eingerichtete Instanz in 24 Stunden',
        'Automatische Updates und Support','Tägliches Backup der Daten','SSL-Verschlüsselung',
        'Benutzerdefiniertes Logo','Benutzerdefinierte Farben',
      ]},
  };

  // triv-input Konfigurationen (Schritt 1 Felder zuerst)
  instanceName = this.createInput('instanceName', 'Instanzname', 'text', {
    suffix: '.insureport.net', pattern: '^[a-z0-9-]{3,}$',
    hint: 'Kleinbuchstaben, Ziffern und Bindestrich, min. 3 Zeichen',
  });
  email = this.createInput('email', 'Ihre E-Mail-Adresse', 'email', {
    autocomplete: 'email', pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
  });

  // Schritt 2 Felder
  username = this.createInput('username', 'Benutzername', 'text', { minlength: 3 });
  password = this.createInput('password', 'Passwort', 'password', { minlength: 8 });
  passwordRepeat = this.createInput('passwordRepeat', 'Passwort wiederholen', 'password', { minlength: 8 });
  companyName = this.createInput('company', 'Firmenname', 'text');
  street = this.createInput('street', 'Straße', 'text');
  houseNumber = this.createInput('houseNumber', 'Hausnummer', 'text');
  postalCode = this.createInput('postalCode', 'PLZ', 'text', { pattern: '^[0-9]{5}$', hint: '5-stellige Postleitzahl' });
  city = this.createInput('city', 'Ort', 'text');

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private reg: RegistrationService
  ) {}

  ngOnInit(): void {
    // Schritt 1 Formular
    this.startForm = this.fb.group({
      instanceName: ['', [Validators.required, Validators.pattern(/^[a-z0-9-]{3,}$/)]],
      email: ['', [Validators.required, Validators.email]],
      termsAccepted: [false, Validators.requiredTrue]
    });

    // Schritt 2 Formular
    this.completeForm = this.fb.group(
      {
        username: ['', [Validators.required, Validators.minLength(3)]],
        password: ['', [Validators.required, Validators.minLength(8)]],
        passwordRepeat: ['', [Validators.required]],
        company: ['', Validators.required],
        street: ['', Validators.required],
        houseNumber: ['', Validators.required],
        postalCode: ['', [Validators.required, Validators.pattern(/^[0-9]{5}$/)]],
        city: ['', Validators.required],
        termsAccepted: [false, Validators.requiredTrue],
        newsletter: [false],
      },
      { validators: this.passwordsMatch }
    );

    // Plan + Token auslesen
    this.route.queryParamMap.subscribe((qp) => {
      const plan = (qp.get('plan') as PlanId) || 'free';
      this.selectedPlan = (['free','basic','comfort','enterprise'] as PlanId[]).includes(plan) ? plan : 'free';
      const cfg = this.PLAN_CONFIG[this.selectedPlan];
      this.startCtaLabel = cfg.cta;
      this.selectedFeatures = cfg.features;

      this.token = qp.get('token');
      // Wenn Token da → direkt Schritt 2 anzeigen
      if (this.token) {
        this.bMailSent = true; // UI: Schritt 1 ausblenden
      }
    });
  }

  // Schritt 1 – Request anstoßen
  async requestMail(): Promise<void> {
    if (this.startForm.invalid) return;
    this.bInProgress = true;
    this.errorMessage = '';
    try {
      await this.reg.startRegistration({
        instanceName: this.startForm.value.instanceName,
        email: this.startForm.value.email,
      }).toPromise();
      this.bMailSent = true; // Hinweis anzeigen: E-Mail gesendet
    } catch (e) {
      this.errorMessage = 'E-Mail konnte nicht angefordert werden. Bitte später erneut versuchen.';
    } finally {
      this.bInProgress = false;
    }
  }

  // Schritt 2 – finale Registrierung
  async completeRegistration(): Promise<void> {
    if (this.completeForm.invalid || !this.token) return;
    this.bInProgress = true;
    this.errorMessage = '';
    try {
      await this.reg.completeRegistration({
        token: this.token,
        username: this.completeForm.value.username,
        password: this.completeForm.value.password,
        company: this.completeForm.value.company,
        street: this.completeForm.value.street,
        houseNumber: this.completeForm.value.houseNumber,
        postalCode: this.completeForm.value.postalCode,
        city: this.completeForm.value.city,
        newsletter: !!this.completeForm.value.newsletter,
      }).toPromise();

      // Erfolg → Login
      this.router.navigate(['/auth/login']);
    } catch (e) {
      this.errorMessage = 'Registrierung konnte nicht abgeschlossen werden. Prüfen Sie den Link oder versuchen Sie es erneut.';
    } finally {
      this.bInProgress = false;
    }
  }

  // Helpers
  private passwordsMatch(group: AbstractControl): ValidationErrors | null {
    const pass = group.get('password')?.value;
    const repeat = group.get('passwordRepeat')?.value;
    return pass === repeat ? null : { mismatch: true };
  }

  private createInput(id: string, label: string, type: string, extras: Partial<Record<string, any>> = {}): DataInput {
    const input = new DataInput();
    input.id = id;
    input.type = type;
    input.title = label;
    input.label = label;
    input.placeholder = label;
    input.required = true;
    input.groupOptions = [];
    input.multipleFiles = false;
    input.filesizeMaxMB = 0;
    input.rightAlignment = false;
    input.step = 1;
    Object.assign(input, extras);
    return input;
  }

  // Vorschau und valid First Step (falls weiterhin genutzt)
  get instanceCtrl() { return this.startForm.get('instanceName')!; }
  get emailCtrl() { return this.startForm.get('email')!; }
  get showPreview(): boolean { return !!(this.instanceCtrl?.value || this.emailCtrl?.value); }
  get instanceDomain(): string {
    const raw = (this.instanceCtrl?.value || '').toString().toLowerCase();
    const safe = raw.replace(/[^a-z0-9-]/g, '').replace(/^-+|-+$/g, '');
    return safe ? `${safe}.insureport.net` : '—';
  }
}
